import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, CheckCircle2, Star, ShieldCheck, Zap, Layers, LineChart,
  Search as SearchIcon, Rocket, Play, Quote, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import Icon from "@/components/site/Icon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import LeadForm from "@/components/site/LeadForm";

const fetchList = (path) => async () => (await api.get(path)).data;

const WHY = [
  { icon: ShieldCheck, title: "Secure by design", desc: "Server-side auth, role-based access and audited APIs baked in from day one." },
  { icon: Zap, title: "Built for speed", desc: "Performance-obsessed engineering tuned for Core Web Vitals and conversions." },
  { icon: Layers, title: "One backend, everywhere", desc: "Your website and Android app share a single secure API and database." },
  { icon: LineChart, title: "Growth-focused", desc: "SEO, analytics and automation wired in so results are measurable, not vague." },
];

const WORKFLOW = [
  { step: "01", title: "Discover", desc: "We learn your goals, users and constraints, then propose a clear plan and quote." },
  { step: "02", title: "Design", desc: "Research-driven UI/UX and a brand system that builds instant trust." },
  { step: "03", title: "Build", desc: "Robust, secure engineering with automated testing and clean architecture." },
  { step: "04", title: "Grow", desc: "Launch, measure and optimize with SEO, analytics and smart automation." },
];

const INDUSTRIES = ["SaaS & Startups", "E-commerce & Retail", "Healthcare", "Finance & Fintech", "Education", "Real Estate", "Hospitality", "Professional Services"];

export default function Home() {
  useSeo({ title: "Premium Digital Solutions Worldwide", description: "Buddy Solution builds websites, web & Android apps, AI automation, branding and growth for businesses worldwide." });

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: fetchList("/content/settings") });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: fetchList("/services") });
  const { data: packages = [] } = useQuery({ queryKey: ["packages"], queryFn: fetchList("/packages") });
  const { data: portfolio = [] } = useQuery({ queryKey: ["portfolio"], queryFn: fetchList("/portfolio") });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: fetchList("/testimonials") });
  const { data: faqs = [] } = useQuery({ queryKey: ["faqs"], queryFn: fetchList("/faqs") });

  const hero = settings?.hero || {};
  const stats = hero.stats || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="bs-aurora pointer-events-none absolute inset-0 -z-10 opacity-70" />
        <div className="bs-grid-bg pointer-events-none absolute inset-0 -z-10" />
        <div className="bs-container grid items-center gap-12 pb-16 pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:pb-24 lg:pt-24">
          <div>
            <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="bs-eyebrow">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> {hero.eyebrow || "Premium Digital Solutions, Worldwide"}
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-5 font-display text-4xl font-black leading-[1.05] sm:text-5xl lg:text-6xl">
              {hero.title || "We build technology that"}{" "}
              <span className="bs-gradient-text">{hero.highlight || "grows your business"}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 max-w-xl text-base text-muted-foreground sm:text-lg">
              {hero.subtitle || "A full-stack digital partner for websites, web & Android apps, AI automation, design and growth."}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full shadow-glow" data-testid="hero-primary-cta">
                <Link to="/contact">Start a Project <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full" data-testid="hero-secondary-cta">
                <Link to="/consultation"><Play className="mr-2 h-4 w-4" /> Get Free Consultation</Link>
              </Button>
            </motion.div>
            <div className="mt-10 grid max-w-md grid-cols-3 gap-6">
              {stats.map((s) => (
                <div key={s.label}>
                  <p className="font-display text-2xl font-extrabold sm:text-3xl">{s.value}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative">
            <div className="absolute -inset-4 -z-10 rounded-[2rem] bg-primary/20 blur-3xl" />
            <div className="rounded-3xl border border-border bs-glass p-3 shadow-soft">
              <div className="overflow-hidden rounded-2xl border border-border">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Analytics dashboard preview" className="aspect-[4/3] w-full object-cover" loading="eager" />
              </div>
              <div className="grid grid-cols-3 gap-2 p-3">
                {[["Uptime", "Monitored"], ["SEO", "Built-in"], ["Apps", "Web + Android"]].map(([a, b]) => (
                  <div key={a} className="rounded-xl border border-border bg-card/70 p-3 text-center">
                    <p className="text-xs font-semibold">{a}</p>
                    <p className="text-[11px] text-muted-foreground">{b}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* trust marquee */}
        <div className="border-y border-border bg-card/30 py-5">
          <div className="bs-container flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Full-stack expertise:</span>
            {["Web", "Android", "AI", "Design", "SEO", "Automation", "E-commerce", "Branding"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bs-container py-20 lg:py-28" id="services">
        <Reveal><SectionHead center eyebrow="What we do" title="Services engineered for outcomes"
          subtitle="From first pixel to sustained growth, our services cover the entire digital lifecycle." /></Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 9).map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.04}>
              <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`}
                className="group block h-full bs-card p-6 hover:-translate-y-1 hover:border-primary/50 hover:shadow-glow">
                <div className="mb-4 inline-flex rounded-xl bg-primary/12 p-3 text-primary transition-transform group-hover:scale-110">
                  <Icon name={s.icon} />
                </div>
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.short_desc}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Learn more <ArrowRight className="ml-1 h-4 w-4" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Button asChild variant="outline" className="rounded-full"><Link to="/services">Explore all services <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>

      {/* WHY */}
      <section className="relative overflow-hidden border-y border-border bg-card/30 py-20 lg:py-28">
        <div className="bs-container grid gap-12 lg:grid-cols-2">
          <Reveal>
            <SectionHead eyebrow="Why Buddy Solution" title="A partner that ships, not just promises"
              subtitle="We combine premium design with serious engineering and honest communication, no fabricated metrics, no guaranteed-ranking gimmicks." />
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full"><Link to="/about">About us</Link></Button>
              <Button asChild variant="outline" className="rounded-full"><Link to="/case-studies">See case studies</Link></Button>
            </div>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.05}>
                <div className="h-full bs-card p-6">
                  <div className="mb-3 inline-flex rounded-xl bg-accent/12 p-2.5 text-accent"><w.icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-base font-bold">{w.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI / TECH */}
      <section className="bs-container py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -inset-3 -z-10 rounded-3xl bg-accent/15 blur-2xl" />
              <img src="https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="AI and automation" className="w-full rounded-3xl border border-border object-cover shadow-soft" loading="lazy" />
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2" delay={0.1}>
            <SectionHead eyebrow="Technology & AI" title="Practical AI that removes real friction"
              subtitle="We integrate LLM assistants, semantic search and intelligent workflows responsibly, with a human in the loop and full audit logs." />
            <ul className="mt-6 space-y-3">
              {["LLM-powered assistants & content", "Smart lead routing and automation", "Retry-safe, logged workflows you control", "Transparent, responsible AI practices"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="border-y border-border bg-card/30 py-20 lg:py-28">
        <div className="bs-container">
          <Reveal><SectionHead center eyebrow="How we work" title="A proven, transparent workflow" /></Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-4">
            {WORKFLOW.map((w, i) => (
              <Reveal key={w.step} delay={i * 0.06}>
                <div className="relative h-full bs-card p-6">
                  <span className="font-mono text-3xl font-bold text-primary/30">{w.step}</span>
                  <h3 className="mt-3 font-display text-lg font-bold">{w.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="bs-container py-20 lg:py-28">
        <Reveal><div className="flex flex-wrap items-end justify-between gap-4">
          <SectionHead eyebrow="Selected work" title="Recent projects & case studies" />
          <Button asChild variant="outline" className="rounded-full"><Link to="/portfolio">View all <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div></Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {portfolio.slice(0, 6).map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.05}>
              <Link to={`/portfolio/${p.slug}`} data-testid={`home-portfolio-${p.slug}`}
                className="group block overflow-hidden bs-card hover:-translate-y-1 hover:shadow-soft">
                <div className="overflow-hidden">
                  <img src={p.image} alt={p.title} className="aspect-[16/11] w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-5">
                  <Badge variant="secondary" className="mb-2">{p.category}</Badge>
                  <h3 className="font-display text-lg font-bold">{p.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{p.summary}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-border bg-card/30 py-20 lg:py-28">
        <div className="bs-container">
          <Reveal><SectionHead center eyebrow="Testimonials" title="What clients say"
            subtitle="Placeholder testimonials shown until verified client quotes are added, we never fabricate reviews." /></Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.id || i} delay={i * 0.06}>
                <div className="flex h-full flex-col bs-card p-6">
                  <Quote className="h-8 w-8 text-primary/30" />
                  <p className="mt-3 flex-1 text-sm leading-relaxed">{t.quote}</p>
                  <div className="mt-5 flex items-center gap-1 text-chart-4">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                  </div>
                  <div className="mt-3">
                    <p className="text-sm font-bold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ""}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bs-container py-20 lg:py-28">
        <Reveal><SectionHead center eyebrow="Industries" title="Trusted across sectors worldwide" /></Reveal>
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          {INDUSTRIES.map((ind) => (
            <span key={ind} className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-medium">{ind}</span>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="border-y border-border bg-card/30 py-20 lg:py-28">
        <div className="bs-container">
          <Reveal><SectionHead center eyebrow="Packages" title="Simple, transparent starting points"
            subtitle="Every engagement is tailored, these packages are a clear starting point, not a hard limit." /></Reveal>
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {packages.map((p, i) => (
              <Reveal key={p.id || i} delay={i * 0.06}>
                <div className={`relative flex h-full flex-col bs-card p-7 ${p.popular ? "border-primary shadow-glow" : ""}`}>
                  {p.popular && <Badge className="absolute -top-3 left-7">Most popular</Badge>}
                  <h3 className="font-display text-xl font-bold">{p.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                  <div className="mt-5">
                    {p.price > 0
                      ? <p className="font-display text-4xl font-black">${p.price.toLocaleString()}<span className="text-sm font-medium text-muted-foreground"> {p.period}</span></p>
                      : <p className="font-display text-4xl font-black">Custom</p>}
                  </div>
                  <ul className="mt-6 flex-1 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}</li>
                    ))}
                  </ul>
                  <Button asChild className="mt-7 w-full rounded-full" variant={p.popular ? "default" : "outline"} data-testid={`pricing-cta-${i}`}>
                    <Link to="/contact">{p.cta}</Link>
                  </Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bs-container py-20 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal><SectionHead eyebrow="FAQ" title="Answers to common questions"
            subtitle="Still curious? Book a free consultation and we'll walk you through it." /></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.id || i} value={`faq-${i}`} data-testid={`home-faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base font-semibold">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA + CONTACT */}
      <section className="bs-container pb-24" id="contact">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-8 sm:p-12">
          <div className="bs-aurora pointer-events-none absolute inset-0 opacity-60" />
          <div className="relative grid gap-10 lg:grid-cols-2">
            <div>
              <span className="bs-eyebrow"><Rocket className="h-3.5 w-3.5 text-primary" /> Let's build together</span>
              <h2 className="mt-4 font-display text-3xl font-extrabold sm:text-4xl">Ready to start your project?</h2>
              <p className="mt-4 text-muted-foreground">
                Tell us about your goals and we'll respond with a clear plan and quote. No pressure, no fabricated promises, just honest expertise.
              </p>
              <div className="mt-6 space-y-3 text-sm">
                {["Free initial consultation", "Transparent, itemized quotes", "Web + Android on one secure backend"].map((t) => (
                  <p key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-success" /> {t}</p>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-background/60 p-6">
              <LeadForm type="contact" services={services} submitLabel="Start a Project" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
